import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { put, select, takeEvery, takeLatest } from "redux-saga/effects";

import EmailFolder from "@mapmycustomers/shared/enum/EmailFolder";
import EmailStatus from "@mapmycustomers/shared/enum/EmailStatus";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Email from "@mapmycustomers/shared/types/email/Email";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { fetchEmailQuota } from "@app/store/email/actions";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganization } from "@app/store/iam";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { formatRawDate } from "@app/util/formatters";

import { PAGE_SIZE, TAB_STATUS_MAP } from "../utils/tabConfig";

import { EmailOutboxViewState } from ".";
import {
  cancelScheduledEmail,
  clearEmailList,
  deleteFailedEmail,
  fetchOutboxEmails,
  fetchOutboxStatusCounts,
  rescheduleEmail,
  retryFailedEmail,
  setOutboxActiveTab,
  setOutboxPage,
  setOutboxSearch,
  setOutboxSort,
} from "./actions";
import { getOutboxActiveTab, getOutboxViewState } from "./selectors";

const messages = defineMessages({
  cancelSuccess: {
    id: "emailOutbox.cancelScheduledEmail.success",
    defaultMessage: "Scheduled email cancelled",
    description: "Success notification when a scheduled email is cancelled",
  },
  deleteSuccess: {
    id: "emailOutbox.deleteFailedEmail.success",
    defaultMessage: "Failed email deleted",
    description: "Success notification when a failed email is deleted",
  },
  listClearSuccess: {
    id: "emailOutbox.clearList.success",
    defaultMessage: "Email list cleared successfully",
    description: "Success notification when a failed email list cleared",
  },
  rescheduleSuccess: {
    id: "emailOutbox.email.reschedule.success",
    defaultMessage: "Email rescheduled",
    description: "Success notification title when a scheduled email is rescheduled",
  },
  rescheduleSuccessDescription: {
    id: "emailOutbox.rescheduleEmail.success.description",
    defaultMessage: "Will be sent on {date}",
    description: "Success notification description when a scheduled email is rescheduled",
  },
  retrySuccess: {
    id: "emailOutbox.retryFailedEmail.success",
    defaultMessage: "Email resubmitted for sending",
    description: "Success notification when a failed email is retried",
  },
});

function* onFetchOutboxEmails() {
  try {
    const organization: Organization = yield select(getOrganization);
    const activeTab: ReturnType<typeof getOutboxActiveTab> = yield select(getOutboxActiveTab);
    const { page, search, sort }: EmailOutboxViewState = yield select(getOutboxViewState);

    const response: ListResponse<Email> = yield callApi("fetchEmails", organization.id, {
      $filters: {
        $and: [
          { status: { $in: [TAB_STATUS_MAP[activeTab]] } },
          { $or: [{ folder: { $eq: EmailFolder.SENT } }, { folder: { $eq: null } }] },
        ],
      },
      $limit: PAGE_SIZE,
      $offset: (page - 1) * PAGE_SIZE,
      $order: `${sort.order === SortOrder.DESC ? "-" : ""}${sort.fieldName}`,
      ...(search ? { $search: search } : {}),
    });

    yield put(
      fetchOutboxEmails.success({
        emails: response.data,
        total: response.total,
      })
    );
  } catch (error) {
    yield put(fetchOutboxEmails.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchOutboxStatusCounts() {
  try {
    const organization: Organization = yield select(getOrganization);
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    const response: AggregatedListResponse<Email, { status: EmailStatus; total: number }[]> =
      yield callApi("fetchEmails", organization.id, {
        $aggs: { primaryGroup: { field: "status" } },
        $filters: {
          $and: [
            {
              createdBy: currentUserId,
            },
            { $or: [{ folder: { $eq: EmailFolder.SENT } }, { folder: { $eq: null } }] },
          ],
        },
        $limit: 0,
      });

    const statusCounts: Record<EmailStatus, number> = (response.aggregations ?? []).reduce(
      (statusCounts: Record<EmailStatus, number>, { status, total }) => ({
        ...statusCounts,
        ...(status ? { [status]: total ?? 0 } : {}),
      }),
      {
        [EmailStatus.CANCELLED]: 0,
        [EmailStatus.ERRORED]: 0,
        [EmailStatus.SCHEDULED]: 0,
        [EmailStatus.SENDING]: 0,
        [EmailStatus.SENT]: 0,
        [EmailStatus.SUBMITTED]: 0,
      }
    );

    yield put(fetchOutboxStatusCounts.success(statusCounts));
  } catch (error) {
    yield put(fetchOutboxStatusCounts.failure());
    yield put(handleError({ error }));
  }
}

function* onViewStateChange() {
  yield put(fetchOutboxStatusCounts.request());
  yield put(fetchEmailQuota.request());
  yield put(fetchOutboxEmails.request());
}

function* onScheduledEmailCancel({
  payload: { emailId },
}: ReturnType<typeof cancelScheduledEmail>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("cancelEmailLogs", organization.id, [emailId]);
    yield put(fetchOutboxEmails.request());
    yield put(fetchOutboxStatusCounts.request());
    yield put(fetchEmailQuota.request());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.cancelSuccess),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onEmailReschedule({
  payload: { emailId, schedule },
}: ReturnType<typeof rescheduleEmail>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("updateEmailLog", organization.id, emailId, {
      id: emailId,
      schedule,
    });
    yield put(fetchOutboxEmails.request());
    yield put(fetchOutboxStatusCounts.request());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage(messages.rescheduleSuccessDescription, {
          date: formatRawDate(schedule.sendAt, "PPp"),
        }),
        message: intl.formatMessage(messages.rescheduleSuccess),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onFailedEmailRetry({ payload: { emailId } }: ReturnType<typeof retryFailedEmail>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("updateEmailLog", organization.id, emailId, {
      id: emailId,
      status: EmailStatus.SUBMITTED,
    });
    yield put(fetchOutboxEmails.request());
    yield put(fetchOutboxStatusCounts.request());
    yield put(fetchEmailQuota.request());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.retrySuccess),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onFailedEmailDelete({ payload: { emailId } }: ReturnType<typeof deleteFailedEmail>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("deleteEmailLogs", organization.id, { emailLogIds: [emailId] });
    yield put(fetchOutboxEmails.request());
    yield put(fetchOutboxStatusCounts.request());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.deleteSuccess),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onClearEmailList({ payload: { status } }: ReturnType<typeof clearEmailList>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("deleteEmailLogs", organization.id, { status });
    yield put(fetchOutboxEmails.request());
    yield put(fetchOutboxStatusCounts.request());
    yield put(fetchEmailQuota.request());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.listClearSuccess),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* emailOutboxSaga() {
  yield takeLatest(fetchOutboxEmails.request, onFetchOutboxEmails);
  yield takeLatest(fetchOutboxStatusCounts.request, onFetchOutboxStatusCounts);
  yield takeLatest(
    [setOutboxActiveTab, setOutboxPage, setOutboxSearch, setOutboxSort],
    onViewStateChange
  );
  yield takeEvery(cancelScheduledEmail, onScheduledEmailCancel);
  yield takeEvery(deleteFailedEmail, onFailedEmailDelete);
  yield takeEvery(rescheduleEmail, onEmailReschedule);
  yield takeEvery(retryFailedEmail, onFailedEmailRetry);
  yield takeEvery(clearEmailList, onClearEmailList);
}
